import React, { useEffect, useState, useRef } from 'react';
import SelectField, { Choice } from 'components/SelectField/SelectField';
import * as api from 'js/api-helper';

interface KontaktPersonResponse {
  readonly oppdragsgivere: KontaktPerson[];
}

interface KontaktPerson {
  readonly id: string;
  readonly navn: string;
}

interface KontaktPersonVelgerProps {
  currentOrg: string;
  onChange: (e: Choice<string> | null) => void;
  initialValue: Choice<string> | null;
  label?: string;
}

function mapKontaktPersonToChoice(person: KontaktPerson): Choice<string> {
  return {
    label: person.navn,
    value: person.id
  };
}

export default function KontaktPersonVelger({ currentOrg, onChange, initialValue, label }: KontaktPersonVelgerProps) {
  const [kontaktPersoner, setKontaktPersoner] = useState<Choice<string>[]>([]);

  // Track previous value to prevent unnecessary updates
  const prevChoiceRef = useRef<Choice<string> | null>(initialValue);

  useEffect(() => {
    if (!currentOrg) return;

    void (async function handleSearch() {
      try {
        const response: KontaktPersonResponse = await api.post(`/api/v1/prosjekt/oppdragsgivereiorganisasjon/${currentOrg}`);

        if (!response.oppdragsgivere) return;

        const newChoices = response.oppdragsgivere.map(mapKontaktPersonToChoice);
        setKontaktPersoner(newChoices);

        // Find initial choice
        const initChoice = newChoices.find((choice) => initialValue && choice.label === initialValue.label) || null;

        // Only call onChange if the value actually changed
        if (prevChoiceRef.current?.value !== initChoice?.value) {
          prevChoiceRef.current = initChoice;
          onChange(initChoice);
        }
      } catch (e) {
        setKontaktPersoner([]);
      }
    })();
  }, [currentOrg, initialValue, onChange]);

  return (
    <div className="KontaktPersonVelger">
      <SelectField
        disabled={!kontaktPersoner.length}
        value={prevChoiceRef.current}
        label={label ?? 'Oppdragsgiver; kontaktPerson'}
        choices={kontaktPersoner}
        onChange={onChange}
      />
    </div>
  );
}
